/**
 * CorrectionPanel - Side panel for correcting entity classification.
 *
 * Shows current classification, allows selecting from effective categories
 * (industry template + custom), and submits corrections to the learning loop.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface CorrectionPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number;
  entityType: "transaction" | "vendor" | "document";
  entityId: number;
  entityDescription?: string;
  currentCategory?: string;
  currentAccountCode?: string;
  currentConfidence?: number;
  onCorrectionSubmitted?: () => void;
}

export function CorrectionPanel({
  open,
  onOpenChange,
  companyId,
  entityType,
  entityId,
  entityDescription,
  currentCategory,
  currentAccountCode,
  currentConfidence,
  onCorrectionSubmitted,
}: CorrectionPanelProps) {
  const [correctedCategory, setCorrectedCategory] = useState("");
  const [correctedAccountCode, setCorrectedAccountCode] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState("");

  const { data: effectiveCategories } =
    trpc.classification.getEffectiveCategories.useQuery(
      { companyId },
      { enabled: open && companyId > 0 }
    );

  const submitMutation = trpc.classification.submitCorrection.useMutation({
    onSuccess: () => {
      toast.success("Correction submitted");
      onOpenChange(false);
      setCorrectedCategory("");
      setCorrectedAccountCode("");
      setCorrectionReason("");
      onCorrectionSubmitted?.();
    },
    onError: err => {
      toast.error(`Failed to submit: ${err.message}`);
    },
  });

  const handleSubmit = () => {
    const category = isCustom ? customCategoryName : correctedCategory;
    if (!category) {
      toast.error("Please select or enter a category");
      return;
    }

    submitMutation.mutate({
      companyId,
      entityType,
      entityId,
      entityDescription,
      originalCategory: currentCategory,
      originalAccountCode: currentAccountCode,
      originalConfidence: currentConfidence,
      correctedCategory: category,
      correctedAccountCode: correctedAccountCode || undefined,
      correctionReason: correctionReason || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Correct Classification</DialogTitle>
          <DialogDescription>
            Your corrections improve future classification accuracy.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Current classification */}
          {currentCategory && (
            <div className="rounded-md bg-muted p-3 text-sm">
              <Label className="text-xs text-muted-foreground">Current</Label>
              <div className="flex items-center gap-2 mt-1">
                <span>{currentCategory}</span>
                {currentAccountCode && (
                  <Badge variant="outline" className="text-xs">
                    {currentAccountCode}
                  </Badge>
                )}
                {currentConfidence !== undefined && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-xs",
                      currentConfidence >= 80
                        ? "bg-success/10 text-success"
                        : currentConfidence >= 50
                          ? "bg-warning/10 text-warning"
                          : "bg-muted text-muted-foreground"
                    )}
                  >
                    {currentConfidence}%
                  </Badge>
                )}
              </div>
              {entityDescription && (
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {entityDescription}
                </p>
              )}
            </div>
          )}

          {/* Category selection */}
          <div className="space-y-2">
            <Label>Correct Category</Label>
            {!isCustom ? (
              <Select
                value={correctedCategory}
                onValueChange={val => {
                  setCorrectedCategory(val);
                  // Auto-fill account code from category
                  const cat = effectiveCategories?.find(c => c.name === val);
                  if (cat) setCorrectedAccountCode(cat.accountCode);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {effectiveCategories?.map(cat => (
                    <SelectItem key={cat.name} value={cat.name}>
                      <span className="flex items-center gap-2">
                        {cat.name}
                        <span className="text-xs text-muted-foreground">
                          {cat.accountCode}
                        </span>
                        {cat.isCustom && (
                          <Badge variant="outline" className="text-[10px]">
                            Custom
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={customCategoryName}
                onChange={e => setCustomCategoryName(e.target.value)}
                placeholder="New category name..."
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setIsCustom(!isCustom)}
            >
              {isCustom ? "Select existing" : "Create new category"}
            </Button>
          </div>

          {/* Account code */}
          <div className="space-y-2">
            <Label>Account Code</Label>
            <Input
              value={correctedAccountCode}
              onChange={e => setCorrectedAccountCode(e.target.value)}
              placeholder="e.g. 6000"
              maxLength={10}
            />
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Textarea
              value={correctionReason}
              onChange={e => setCorrectionReason(e.target.value)}
              placeholder="Why is this correction needed?"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitMutation.isPending}>
            {submitMutation.isPending ? "Submitting..." : "Submit Correction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
